// client/src/pages/Admin/ActivityLog/components/ActivityTable.tsx

import React from 'react';
import {
    LogIn,
    LogOut,
    AlertCircle,
    KeyRound,
    Lock,
    FilePlus,
    FileEdit,
    FileMinus,
    CheckCircle,
    Archive,
    FolderPlus,
    FolderEdit,
    FolderMinus,
    HandCoins,
    Edit,
    Trash2,
    RefreshCw,
    Mail,
    Tag,
    CreditCard,
    XCircle,
    AlertTriangle,
} from 'lucide-react';
import { ActivityLog, ActivityAction } from '../types/activity.types';
import { ACTION_LABELS, ACTION_ICON_NAMES } from '../constants/activity.constants';

interface ActivityTableProps {
    activities: ActivityLog[];
    loading?: boolean;
}

// Map icon names to Lucide components
const ICON_MAP: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
    LogIn,
    LogOut,
    AlertCircle,
    KeyRound,
    Lock,
    FilePlus,
    FileEdit,
    FileMinus,
    CheckCircle,
    Archive,
    FolderPlus,
    FolderEdit,
    FolderMinus,
    HandCoins,
    Edit,
    Trash2,
    RefreshCw,
    Mail,
    Tag,
    CreditCard,
    XCircle,
    AlertTriangle,
};

const getIcon = (action: ActivityAction) => {
    const iconName = ACTION_ICON_NAMES[action] || 'AlertCircle';
    return ICON_MAP[iconName] || ICON_MAP.AlertCircle;
};

const getSeverityColor = (severity: string): string => {
    const colorMap: Record<string, string> = {
        info: 'bg-blue-100 text-blue-700',
        warning: 'bg-yellow-100 text-yellow-700',
        error: 'bg-red-100 text-red-700',
        critical: 'bg-red-200 text-red-800',
    };
    return colorMap[severity] || colorMap.info;
};

export const ActivityTable: React.FC<ActivityTableProps> = ({ activities, loading }) => {
    if (loading) {
        return (
            <div className="loading-state">
                <div className="spinner">⏳</div>
                <p>Loading activities...</p>
            </div>
        );
    }

    if (activities.length === 0) {
        return (
            <div className="empty-state">
                <p>No activities found.</p>
            </div>
        );
    }

    return (
        <div className="activity-table-wrapper">
            <table className="activity-table">
                <thead>
                    <tr>
                        <th>Action</th>
                        <th>User</th>
                        <th>Description</th>
                        <th>Severity</th>
                        <th>Timestamp</th>
                    </tr>
                </thead>
                <tbody>
                    {activities.map((activity) => {
                        const Icon = getIcon(activity.action);
                        const severityColor = getSeverityColor(activity.severity);
                        const label = ACTION_LABELS[activity.action] || activity.action;

                        return (
                            <tr key={activity._id}>
                                <td>
                                    <div className="action-cell">
                                        <Icon size={16} className="action-icon" />
                                        <span>{label}</span>
                                    </div>
                                </td>
                                <td>
                                    <span className="username">{activity.username}</span>
                                </td>
                                <td>
                                    <span className="description">
                                        {activity.details?.description || 'No description'}
                                    </span>
                                </td>
                                <td>
                                    <span className={`severity-badge ${severityColor}`}>
                                        {activity.severity}
                                    </span>
                                </td>
                                <td>
                                    <span className="timestamp">
                                        {new Date(activity.timestamp).toLocaleString()}
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ActivityTable;
